import { useContext, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

import classNames from 'classnames'
import toast from 'cogo-toast'
import { getCookie } from 'cookies-next'
import copy from 'copy-to-clipboard'
import { DomainStatus, GetSidebarWorkspaceById_workspace } from 'globalTypes'
import { useIntercom } from 'react-use-intercom'

import { gql, useQuery } from '@apollo/client'
import {
  faHome,
  faPlus,
  faChevronUp,
  faTimes,
  faIdBadge,
  faGear,
  faCircleQuestion,
  faArrowRightFromBracket,
  faCheck,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Chip from 'Root/App/Item/Chip'
import ItemVisibility from 'Root/App/Item/ItemVisibility'
import Paragraph from 'Root/App/Paragraph'
import PreTitle from 'Root/App/PreTitle'
import Title from 'Root/App/Title'
import { BoardContext } from 'Root/Pages/Layout/BoardContext'
import { useHideUiForConsumer } from 'lib/use-hide-ui-for-consumer'
import Link from 'next/link'
import { useRouter } from 'next/router'

const avatarSvg = '/default-brand-logo.svg'

interface UserWorkspacePayload {
  id: string
  name: string
  role: string
  slug: string
  brandName?: string
  brandLogoSmall?: string
  autologin_link: string
}

interface WorkspacesListForUserResponse {
  workspacesListForUser: UserWorkspacePayload[]
}

const GET_WORKSPACE_BY_ID = gql`
  query GetSidebarWorkspaceById($workspaceId: ID!) {
    workspace(id: $workspaceId) {
      id
      brandLogoSmall
      brandName
      domain {
        name
        status
      }
      hideSupportUIForConsumer
      slug
      user {
        id
        vanity_name
      }
    }
  }
`

const GET_WORKSPACES_LIST_FOR_USER = gql`
  query GetWorkspacesListForUser {
    workspacesListForUser {
      id
      name
      role
      slug
      brandName
      brandLogoSmall
      autologin_link
    }
  }
`

export const GET_USER_BOARDS = gql`
  query GetUserBoards(
    $createdByMe: Boolean
    $limit: Int
    $offset: Int
    $workspaceUserId: String
    $filterByElementType: [ElementTypeFilter]
  ) {
    elements(
      createdByMe: $createdByMe
      limit: $limit
      offset: $offset
      workspaceUserId: $workspaceUserId
      filterByElementType: $filterByElementType
    ) {
      ... on Folder {
        totalContents: totalContentsForType(type: content)
        totalCollections: totalContentsForType(type: collection)
        canManage: can(permission: "manage")
        canRead: can(permission: "read")

        id
        slug
        title
        visibility
      }
    }

    me {
      id
      email
      firstname
      lastname
      name
      picture
      vanity_name
      workspace {
        id
        brandName
        brandLogoSmall
        domain {
          name
          status
        }
        slug
      }
      subscriptionPlan {
        code
      }
    }

    totalElements(
      createdByMe: $createdByMe
      workspaceUserId: $workspaceUserId
      filterByElementType: [{ value: content }, { value: collection }]
    )
  }
`

interface WorkspaceInfo {
  brandName?: string
  brandLogoSmall?: string
  domain?: {
    name?: string
    status?: DomainStatus
  }
  slug?: string
}

const SidebarAvatar = ({
  workspaceInfo,
  loading,
  showCloseButton,
  onClose,
}: {
  workspaceInfo?: WorkspaceInfo
  loading: boolean
  showCloseButton?: boolean
  onClose?: () => void
}) => {
  const { i18n } = useLingui()
  const avatarTitle = workspaceInfo?.brandName
    ? workspaceInfo?.brandName
    : 'Taylora'
  const avatarLogo = workspaceInfo?.brandLogoSmall
    ? workspaceInfo?.brandLogoSmall
    : avatarSvg
  const mainDomain = process.env.NEXT_PUBLIC_DOMAIN || 'taylora.com'
  const activeCustomDomain =
    workspaceInfo?.domain?.status === DomainStatus.active
      ? workspaceInfo?.domain?.name
      : undefined
  const workspaceDomain = activeCustomDomain ?? mainDomain
  const workspaceLink = workspaceInfo?.slug
    ? `https://${workspaceDomain}/w/${workspaceInfo.slug}`
    : `https://${workspaceDomain}`
  const handleCopyWorkspaceLink = () => {
    copy(workspaceLink)
    toast.success(t(i18n)`Link copiato negli appunti`)
  }

  return (
    <div className="relative w-full p-6 bg-white rounded-3xl inline-flex flex-col justify-center items-center gap-6">
      {showCloseButton && onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close sidebar"
          style={{ touchAction: 'manipulation' }}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700 flex items-center justify-center sm:hidden"
        >
          <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
        </button>
      )}

      <div className="w-full flex flex-col justify-start items-center gap-3 min-w-0">
        <div className="w-14 h-14 rounded-2xl inline-flex justify-start items-center overflow-hidden">
          {!loading ? (
            <img
              alt="sidebar-avatar"
              className="w-full h-full rounded-full object-cover"
              src={avatarLogo}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 animate-pulse rounded-full" />
          )}
        </div>

        <div className="w-full flex flex-col justify-start items-start gap-0.5 min-w-0">
          <div className="w-full flex flex-col justify-center items-center gap-3 min-w-0">
            <div className="w-full flex flex-col justify-start items-start min-w-0">
              {!loading ? (
                <Title
                  size="2XS"
                  className="w-full text-center text-gray-900 truncate"
                >
                  {avatarTitle}
                </Title>
              ) : (
                <div className="w-40 h-7 bg-gray-200 animate-pulse rounded-lg" />
              )}
            </div>
          </div>

          <Paragraph className="w-full text-center text-gray-500 text-sm leading-5">
            Learning Workspace
          </Paragraph>
        </div>
      </div>

      {!loading ? (
        <div className="w-full min-w-0 flex justify-center items-center">
          <button
            type="button"
            className="w-full min-w-0 text-center text-gray-500 text-xs font-medium leading-4 truncate cursor-copy"
            onClick={handleCopyWorkspaceLink}
            title={workspaceLink}
            aria-label={workspaceLink}
          >
            {workspaceLink}
          </button>
        </div>
      ) : (
        <div className="w-44 h-4 bg-gray-200 animate-pulse rounded" />
      )}
    </div>
  )
}

const SidebarUserMenuPanel = ({
  user,
  workspace,
  onClose,
  onClickCallback,
  workspacesList,
  currentWorkspaceId,
  loadingWorkspaces,
}: {
  user: any
  workspace: GetSidebarWorkspaceById_workspace | null
  onClose: () => void
  onClickCallback: () => void
  workspacesList: UserWorkspacePayload[] | undefined
  currentWorkspaceId: string | null
  loadingWorkspaces: boolean
}) => {
  const hideHelpForConsumer = useHideUiForConsumer({
    hideSupportUI: workspace?.hideSupportUIForConsumer,
  })
  const workspaceRole = getCookie('workspace_role')

  return (
    <div className="px-4 pt-4 pb-6 bg-white rounded-3xl sm:rounded-lg flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end items-center">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close sidebar"
            style={{ touchAction: 'manipulation' }}
            className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>
        <div className="pb-6 border-b border-gray-200 flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <div className="relative w-14 h-14">
              <div className="w-14 h-14 rounded-2xl overflow-hidden">
                <img
                  className="w-14 h-14 rounded-full object-cover"
                  src={user?.picture}
                  alt="user-avatar"
                />
              </div>
              {user?.subscriptionPlan?.code === 'partner' && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-white text-white text-xs font-bold rounded-full flex items-center justify-center">
                  <img
                    className="w-5 h-5 rounded-full object-cover"
                    src="/partner-badge.svg"
                    alt=""
                    aria-hidden="true"
                  />
                </span>
              )}
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <Title size="2XS" className="text-center text-gray-900">
                {`${user.firstname} ${user.lastname}`}
              </Title>
              <Paragraph size="S" className="text-center text-gray-500">
                {user?.email}
              </Paragraph>
            </div>
          </div>
          <div className="flex items-start gap-3">
            {workspaceRole === 'creator' && (
              <Link
                href={`/profile/${user.vanity_name}`}
                onClick={() => {
                  onClickCallback?.()
                  onClose?.()
                }}
                className="h-10 pl-2.5 pr-4 bg-gray-900 rounded-full flex items-center gap-0.5"
              >
                <div className="p-1 rounded-md flex items-center">
                  <FontAwesomeIcon
                    icon={faIdBadge}
                    className="w-4 h-4 text-gray-50"
                  />
                </div>
                <span className="text-gray-50 text-sm font-semibold">
                  <Trans>Profilo</Trans>
                </span>
              </Link>
            )}

            <Link
              href="/settings"
              onClick={() => {
                onClickCallback?.()
                onClose?.()
              }}
              className="pl-2.5 pr-4 py-2 rounded-full border-2 border-gray-300 flex items-center gap-0.5"
            >
              <div className="p-1 rounded-md flex items-center">
                <FontAwesomeIcon
                  icon={faGear}
                  className="w-4 h-4 text-gray-500"
                />
              </div>
              <span className="text-gray-500 text-sm font-semibold">
                <Trans>Impostazioni</Trans>
              </span>
            </Link>
          </div>
        </div>
      </div>

      {(loadingWorkspaces || (workspacesList && workspacesList.length > 0)) && (
        <div className="flex flex-col gap-3">
          <div className="px-2">
            <Trans>
              <span className="text-xs font-semibold uppercase leading-4 text-gray-800">
                Learning Workspace
              </span>{' '}
              <span className="text-xs font-semibold uppercase leading-4 text-gray-500">
                Disponibili
              </span>
            </Trans>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex flex-col gap-1.5 border-b border-gray-200 pb-4">
              {loadingWorkspaces ? (
                <div className="h-10 p-2 rounded-xl flex items-center gap-3">
                  <div className="w-6 h-6 rounded-md bg-gray-200 animate-pulse flex-shrink-0" />
                  <div className="flex-1 h-4 bg-gray-200 animate-pulse rounded" />
                </div>
              ) : (
                workspacesList?.map((workspace) => {
                  const isActive = workspace.id === currentWorkspaceId
                  if (isActive) {
                    return (
                      <div
                        key={workspace.id}
                        className="h-10 p-2 bg-gray-100 rounded-xl flex items-center gap-3"
                      >
                        <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            className="w-full h-full object-cover"
                            src={workspace.brandLogoSmall ?? avatarSvg}
                            alt={workspace.brandName}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Title size="2XS" className="truncate">
                            {workspace.brandName}
                          </Title>
                        </div>
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="w-4 h-4 text-gray-500 flex-shrink-0"
                        />
                      </div>
                    )
                  }
                  return (
                    <Link
                      key={workspace.id}
                      href={workspace.autologin_link}
                      onClick={() => {
                        onClickCallback?.()
                        onClose?.()
                      }}
                      className="p-2 rounded-xl flex items-center gap-3"
                    >
                      <div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          className="w-full h-full object-cover"
                          src={workspace.brandLogoSmall ?? avatarSvg}
                          alt={workspace.brandName}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Title size="2XS" className="truncate">
                          {workspace.brandName}
                        </Title>
                      </div>
                    </Link>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {!hideHelpForConsumer && (
          <a
            href="https://intercom.help/taylora-com/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 flex items-center gap-0.5 hover:bg-gray-100 rounded-md py-1"
          >
            <div className="p-1 rounded-md flex items-center">
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="w-4 h-4 text-gray-500"
              />
            </div>
            <Paragraph size="S" className="flex-1 text-gray-500">
              <Trans>Assistenza</Trans>
            </Paragraph>
          </a>
        )}
        <Link
          href="/logout"
          className="px-2 flex items-center gap-0.5 cursor-pointer hover:bg-gray-100 rounded-md py-1"
        >
          <div className="p-1 rounded-md flex items-center">
            <FontAwesomeIcon
              icon={faArrowRightFromBracket}
              className="w-4 h-4 text-gray-500"
            />
          </div>
          <Paragraph size="S" className="flex-1 text-gray-500">
            <Trans>Esci</Trans>
          </Paragraph>
        </Link>
      </div>
    </div>
  )
}

const SidebarUserMenu = ({
  user,
  loading,
  workspace,
  onClickCallback,
  sidebarEl,
}: {
  user: any
  loading: boolean
  workspace: GetSidebarWorkspaceById_workspace | null
  onClickCallback: () => void
  sidebarEl: HTMLElement | null
}) => {
  const { update } = useIntercom()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [menuStyle, setMenuStyle] = useState<{
    wrapper: React.CSSProperties
    panel: React.CSSProperties
  }>({
    wrapper: { position: 'fixed', visibility: 'hidden' },
    panel: {},
  })
  const currentWorkspaceId = workspace?.id || user?.workspace?.id || null

  useEffect(() => {
    if (Boolean(user)) {
      if (window.Intercom) {
        update({
          email: user.email,
          name: user.firstname,
          userId: user.id,
        })
      }
    }
  }, [user, update])

  const { data, loading: loadingWorkspacesListData } =
    useQuery<WorkspacesListForUserResponse>(GET_WORKSPACES_LIST_FOR_USER, {
      skip: !showUserMenu,
    })

  const isLoading = loading || loadingWorkspacesListData

  // Recalculate user menu position on open and on window resize, to ensure it won't be clipped by the viewport
  const recalcUserPanelPosition = useCallback(() => {
    if (!triggerRef.current || !panelRef.current || !sidebarEl) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const sidebarRect = sidebarEl.getBoundingClientRect()
    const panelHeight = panelRef.current.scrollHeight
    const MARGIN = 8

    const idealTop = triggerRect.top - panelHeight - MARGIN
    const top = Math.max(sidebarRect.top + MARGIN, idealTop)
    const maxHeight = triggerRect.top - MARGIN - top

    setMenuStyle({
      wrapper: {
        position: 'fixed',
        top,
        left: sidebarRect.left + MARGIN,
        width: sidebarRect.width - MARGIN * 2,
        height: maxHeight,
        overflow: 'hidden',
        zIndex: 9999,
      },
      panel: {
        maxHeight,
        overflowY: 'auto',
      },
    })
  }, [sidebarEl])

  useEffect(() => {
    if (!showUserMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      )
        return
      setShowUserMenu(false)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserMenu(false)
    }

    window.addEventListener('resize', recalcUserPanelPosition)
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', recalcUserPanelPosition)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showUserMenu, recalcUserPanelPosition])

  useEffect(() => {
    if (showUserMenu) {
      setIsMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true))
      })
    } else {
      setIsVisible(false)
    }
  }, [showUserMenu])

  useEffect(() => {
    if (isMounted) recalcUserPanelPosition()
  }, [isMounted, recalcUserPanelPosition])

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isVisible) setIsMounted(false)
  }

  if (!user) return null

  return (
    <div className="w-full">
      {isMounted &&
        createPortal(
          <div
            style={{
              ...menuStyle.wrapper,
              pointerEvents: isVisible ? 'auto' : 'none',
            }}
          >
            <div
              ref={panelRef}
              style={{
                ...menuStyle.panel,
                transition: 'transform 300ms ease-in-out',
                transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              <SidebarUserMenuPanel
                user={user}
                workspace={workspace}
                onClose={() => setShowUserMenu(false)}
                onClickCallback={onClickCallback}
                workspacesList={data?.workspacesListForUser}
                currentWorkspaceId={currentWorkspaceId}
                loadingWorkspaces={loadingWorkspacesListData}
              />
            </div>
          </div>,
          document.body
        )}

      <div
        ref={triggerRef}
        className={classNames(
          'self-stretch p-4 rounded-xl w-full flex items-center justify-between bg-gray-100 transition-colors cursor-pointer',
          { 'bg-gray-200': showUserMenu }
        )}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={showUserMenu}
        tabIndex={0}
        onClick={() => setShowUserMenu((v) => !v)}
      >
        <div className="flex-1 overflow-hidden mr-2">
          <Chip
            className="inline-flex items-center w-full"
            icon={
              <div className="w-10 h-10 relative rounded-full flex-shrink-0">
                {!isLoading ? (
                  <img
                    alt="sidebar-user-menu"
                    className="w-10 h-10 rounded-lg"
                    src={user?.picture}
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-200 animate-pulse rounded-lg" />
                )}
              </div>
            }
          >
            <div className="min-w-0 flex-1 ml-3">
              {!isLoading ? (
                <Title size="2XS" className="truncate">
                  {`${user.firstname} ${user.lastname}`}
                </Title>
              ) : (
                <div className="w-24 h-6 bg-gray-200 animate-pulse rounded-lg" />
              )}
              <Paragraph className="text-gray-500 truncate">
                {user?.email}
              </Paragraph>
            </div>
          </Chip>
        </div>

        <div className="flex items-center">
          <FontAwesomeIcon
            icon={faChevronUp}
            className={classNames('w-4 h-4 text-gray-500')}
          />
        </div>
      </div>
    </div>
  )
}

interface SidebarMenuItemProps {
  href: string
  title: string
  icon: React.ReactNode
  count: number
  isActive?: boolean
  loading?: boolean
  onClickCallback: () => void
}

const SidebarMenuItem = ({
  href,
  title,
  icon,
  count,
  isActive = false,
  loading = false,
  onClickCallback,
}: SidebarMenuItemProps) => {
  return (
    <Link
      href={href}
      className={classNames([
        'self-stretch h-10 pr-2 rounded-full flex items-center',
        {
          'bg-gray-900 hover:bg-gray-900': isActive,
        },
      ])}
      onClick={onClickCallback}
    >
      <div className="p-2.5 rounded-tl-lg rounded-bl-lg flex justify-center items-center overflow-hidden">
        {icon}
      </div>
      <div
        className={classNames([
          'flex-1 mx-2 text-left text-sm font-semibold leading-tight truncate',
          'overflow-hidden',
          'whitespace-nowrap',
          { 'text-white': isActive },
          { 'text-gray-900': !isActive },
        ])}
        title={title}
      >
        {title}
      </div>
      <div
        className={classNames([
          'h-6 min-w-6 px-2 rounded-full flex justify-center items-center flex-shrink-0',
          { 'bg-white': isActive },
          { 'bg-gray-900': !isActive },
        ])}
      >
        <div
          className={classNames([
            'text-center justify-center text-sm font-semibold leading-tight',
            { 'animate-pulse': loading },
            { 'text-gray-900': isActive },
            { 'text-white': !isActive },
          ])}
        >
          {count}
        </div>
      </div>
    </Link>
  )
}

const Sidebar: React.FC<{ open?: boolean }> = () => {
  const [sidebarEl, setSidebarEl] = useState<HTMLElement | null>(null)
  const { asPath } = useRouter()
  const currentPath = asPath.split('?')[0].split('#')[0]
  const { hideBoard, showSidebarButton } = useContext(BoardContext)
  const workspaceRole = getCookie('workspace_role')
  const workspaceId = (getCookie('workspace_id') as string) || null

  const { data: workspaceByIdData, loading: loadingWorkspaceByIdData } =
    useQuery(GET_WORKSPACE_BY_ID, {
      variables: { workspaceId },
      skip: !workspaceId,
    })

  const { loading, data } = useQuery(GET_USER_BOARDS, {
    variables: {
      createdByMe: null,
      limit: 50,
      offset: 0,
      workspaceUserId: workspaceByIdData?.workspace?.user?.id ?? null,
      filterByElementType: [{ value: 'folder' }],
    },
    skip: workspaceId ? loadingWorkspaceByIdData : false,
    fetchPolicy: 'cache-first',
  })

  const workspace = workspaceByIdData?.workspace ?? data?.me?.workspace

  const workspaceSlug = workspace?.slug
  const isHomeActive = workspaceSlug
    ? currentPath === `/w/${workspaceSlug}`
    : false

  const isLoading = loading || loadingWorkspaceByIdData

  return (
    <aside
      className={
        'p-2 bg-gray-100 rounded-2xl flex flex-col h-full w-full shrink-0 overflow-hidden'
      }
      ref={setSidebarEl}
    >
      <div className="flex-1 w-full flex flex-col min-h-0">
        <div className="rounded-xl flex flex-col justify-start items-start gap-12 shrink-0">
          <div className="w-full flex justify-between items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0 overflow-hidden">
              <SidebarAvatar
                workspaceInfo={
                  workspaceByIdData?.workspace ?? data?.me?.workspace
                }
                loading={isLoading}
                showCloseButton={showSidebarButton}
                onClose={hideBoard}
              />
            </div>
          </div>
        </div>

        <div className="px-4 mt-6 shrink-0">
          <SidebarMenuItem
            href={'/'}
            title="Home"
            icon={
              <FontAwesomeIcon
                className={classNames([
                  'w-5 h-5 text-center justify-center text-sm font-black leading-tight',
                  { 'text-white': isHomeActive },
                  { 'text-gray-900': !isHomeActive },
                ])}
                icon={faHome}
              />
            }
            count={data?.totalElements || 0}
            isActive={isHomeActive}
            loading={isLoading}
            onClickCallback={hideBoard}
          />
        </div>

        {!(workspaceRole === 'consumer' && data?.elements?.length === 0) && (
          <div className="px-4 mt-6 shrink-0">
            <PreTitle className="w-60 flex flex-col justify-start items-start">
              <div className="self-stretch justify-start text-gray-500 leading-none">
                <Trans>Bacheche</Trans>
              </div>
            </PreTitle>
          </div>
        )}

        <div className="overflow-y-auto px-4 mt-2 min-h-0">
          <div className="flex flex-col gap-2">
            {data?.elements?.map((folder) => (
              <SidebarMenuItem
                key={folder.id}
                href={`/f/${folder.slug}`}
                title={folder.title}
                icon={
                  <ItemVisibility
                    id={folder.id}
                    wrapperClassName="flex"
                    iconClassName={classNames([
                      'w-5 h-5 text-center justify-center text-sm font-black leading-tight',
                      { 'text-white': currentPath === `/f/${folder.slug}` },
                      { 'text-gray-900': currentPath !== `/f/${folder.slug}` },
                    ])}
                  />
                }
                count={folder.totalContents + folder.totalCollections}
                isActive={currentPath === `/f/${folder.slug}`}
                loading={isLoading}
                onClickCallback={hideBoard}
              />
            ))}
            {workspaceRole !== 'consumer' && (
              <Link
                href="/folders/create"
                className="self-stretch w-full h-10 pr-2 rounded-full inline-flex justify-start items-center gap-3 overflow-hidden"
                onClick={hideBoard}
              >
                <div className="flex justify-start items-center">
                  <div className="p-2.5 rounded-tl-lg rounded-bl-lg flex justify-center items-center overflow-hidden">
                    <FontAwesomeIcon
                      icon={faPlus}
                      className="w-5 h-5 text-center justify-center text-gray-500 text-sm font-black leading-tight"
                    />
                  </div>
                  <div className="text-center justify-center text-gray-500 text-sm font-semibold leading-tight">
                    <Trans>Aggiungi bacheca</Trans>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* Spacer to push user menu to bottom */}
        <div className="flex-1" />
      </div>

      <div className="w-full">
        <SidebarUserMenu
          user={data?.me}
          loading={isLoading}
          workspace={workspaceByIdData?.workspace}
          onClickCallback={hideBoard}
          sidebarEl={sidebarEl}
        />
      </div>
    </aside>
  )
}

export default Sidebar
